import { Box, Heading } from '@radix-ui/themes';
import { BadgeCurrency } from 'components/common/badges/currency';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { FormTextField } from 'components/form/textfield';
import { ActionSelect } from 'components/layout/action-select';
import { Details } from 'components/layout/details';
import { EB } from 'components/layout/error-boundary';
import { SecurityForm } from 'components/main/routes/securities/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { SecurityWhole } from 'lib/interfaces/database/security';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useSecuritiesStore from 'stores/securities';

export const Securities = () => {
  const { data, create, load, update } = useSecuritiesStore();

  const { perms } = useContext(AuthContext);
  const { securities: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [dialog, setDialog] = useState<DialogState>();

  const [search, setSearch] = useState('');

  const filteredData = useMemo(() => {
    if (!search.trim()) {
      return data;
    }

    const lowerSearch = search.toLowerCase();

    return data.filter((d) => {
      if (d.name.toLowerCase().includes(lowerSearch)) {
        return true;
      }

      if (d.code.toLowerCase().includes(lowerSearch)) {
        return true;
      }

      if (d.currency.toLowerCase().includes(lowerSearch)) {
        return true;
      }

      if (d.notes?.toLowerCase().includes(lowerSearch)) {
        return true;
      }

      return false;
    });
  }, [search, data]);

  return (
    <EB>
      <Heading>Securities</Heading>

      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        toolbarControls={[
          <FormTextField
            placeholder="Search"
            defaultValue={search}
            onChange={setSearch}
            className="width-250px"
            hideLabel
          />,
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: SecurityWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: SecurityWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: SecurityWhole, b: SecurityWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Name',
            rowFn: (item: SecurityWhole) => item.name,
            sortFn: (a: SecurityWhole, b: SecurityWhole) =>
              SortUtil.strings(a.name, b.name),
          },
          {
            header: 'Code',
            rowFn: (item: SecurityWhole) => item.code,
            sortFn: (a: SecurityWhole, b: SecurityWhole) =>
              SortUtil.strings(a.code, b.code),
          },
          {
            header: 'Currency',
            rowFn: (item: SecurityWhole) => (
              <BadgeCurrency value={item.currency} />
            ),
            sortFn: (a: SecurityWhole, b: SecurityWhole) =>
              SortUtil.strings(a.currency, b.currency),
          },
          {
            header: 'Notes',
            rowFn: (item: SecurityWhole) => (
              <Box style={{ maxWidth: '300px' }}>
                {item.notes && <Details>{item.notes}</Details>}
              </Box>
            ),
          },
        ]}
        data={filteredData}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<SecurityForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<SecurityForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as BaseDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </EB>
  );
};
